import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Collapse, FormControlLabel, Stack, Typography } from '@mui/material'
import React from 'react'
import { useDocumentOptions } from 'src/Routes/Service/Documents/useDocument'
import { makeStyles } from 'tss-react/mui'
import { DownloadsParent } from '../../models'
import { CopyToClipboard } from 'src/Elements/CopyToClipboard'
import SubcategoryRestrictions from './SubcategoryRestrictions'
import { useFormContext, useWatch } from 'react-hook-form'
import { Checkbox, MultiSelect, TextField, Toggle } from 'src/Libs/inputs/react-hook-form'
import Select from 'src/Libs/inputs/react-hook-form/Select/Select'

const useStyles = makeStyles()(theme => ({
  toggleLabel: {
    ...(theme.typography.h5 as React.CSSProperties),
  },
  urlNameDisplay: {
    ...(theme.typography.caption as React.CSSProperties),
    backgroundColor: theme.palette.grey.grey07 + ' !important',
  },
  urlNameDisplayBorder: {
    borderColor: theme.palette.grey.grey06 + ' !important',
  },
  urlNameDisplayText: {
    WebkitTextFillColor: theme.palette.grey.grey00 + ' !important',
  },
}))

interface Props {
  parent?: DownloadsParent
}

/**
 * Form for updating files
 */
const DownloadItemForm: React.FC<Props> = ({ parent }) => {
  const { t } = useCustomTranslation()
  const { languageOptions, countryOptions } = useDocumentOptions()
  const { classes } = useStyles()
  const parentRestrictsUserLevels = parent && (!parent.visibleToCustomer || !parent.visibleToInstaller)
  const isPublic = useWatch({ name: 'isPublic' })
  const urlName = useWatch({ name: 'urlName' })
  const { setValue } = useFormContext()
  const previewUrl = isPublic
    ? `${process.env.REACT_APP_PUBLIC_FILES_DOMAIN}/${urlName}`
    : `${process.env.REACT_APP_BASEURL}/downloads?file=${urlName}`

  const transformUrlName = (): void => {
    setValue('urlName', urlName.trim().replaceAll(/\s+/g, '-'))
  }

  return (
    <Stack direction={'column'} spacing={2} sx={{ mt: 2 }}>
      <TextField name="fileName" label={t('documents.add-category.form.name')} required fullWidth />
      <MultiSelect
        name="countryList"
        options={countryOptions}
        label={t('documents.add-category.form.country')}
        data-qa={'create-category-select-documents-country'}
        required
        clearAllLabel={t('downloads.country.clear-all')}
        selectAllLabel={t('downloads.country.select-all')}
      />
      <Select
        name="language"
        options={languageOptions}
        label={t('documents.add-category.form.language')}
        required
        data-qa={'create-category-select-documents-language'}
      />
      <Box>
        <Typography variant="caption" sx={{ pl: 1 }}>
          {t('documents.add-category.form.checkbox-label')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Checkbox
            name="visibleToCustomer"
            label={t('documents.add-category.form.visible-to-customer')}
            disabled={parent ? !parent.visibleToCustomer : false}
          />
          <Checkbox
            name="visibleToInstaller"
            label={t('documents.add-category.form.visible-to-installer')}
            disabled={parent ? !parent.visibleToInstaller : false}
          />
          <Checkbox name="visibleToService" label={t('documents.add-category.form.visible-to-service')} checked={true} disabled />
        </Stack>
      </Box>
      {parentRestrictsUserLevels && (
        <SubcategoryRestrictions parentVisibleToCustomer={!!parent?.visibleToCustomer} parentVisibleToInstaller={!!parent?.visibleToInstaller} />
      )}
      <FormControlLabel
        control={<Toggle name="isPublic" />}
        labelPlacement="start"
        label={<span className={classes.toggleLabel}>{t('documents.add-category.form.public-file')}</span>}
        sx={{ width: '100%', justifyContent: 'space-between' }}
      />
      <Stack spacing={3}>
        <TextField
          required={isPublic}
          name="urlName"
          label={isPublic ? t('documents.add-category.form.url-name') : t('documents.add-category.form.protected-url-name')}
          fullWidth
          onBlur={transformUrlName}
        />
        <Collapse in={!!urlName}>
          <CopyToClipboard
            label=""
            value={urlName ? previewUrl : ''}
            disabled
            iconColor={'grey.grey00'}
            classes={{
              root: classes.urlNameDisplay,
              notchedOutline: classes.urlNameDisplayBorder,
              input: classes.urlNameDisplayText,
            }}
          />
        </Collapse>
      </Stack>
    </Stack>
  )
}

export default DownloadItemForm
